import type { PostFetchFnType } from "../../../../common-deprecated/utils/registryTypes";
import type { BuildAndBuyStoreType } from "../redux/store";
import { NavStep } from "../../shared-logic/utils/Navigation";
import { calculatePrice, initBasket } from "../redux/actions/BasketActions";
import { enableExternalPrice, setExternalPriceFunctionFetch } from "../redux/actions/ExternalPriceActions";
import { enableExternalPriceCalculation } from "../utils/settings/bnbSettingUtils";
import { setFinanceOption, setIsBuild } from "../../../../common-deprecated/redux/actions/UtilActions";
import type { FinanceOptionType } from "../../../../common-deprecated/types/CommonTypes";
import { showBnbMonthlyRates } from "../utils/settings/bnbFinanceSettingUtils";
import { COMPONENT_PDF } from "../../../../shared-logic/server/components";
import { initModelFilter } from "../redux/actions/ModelFilterActions";
import { setFinanceProductType, setFinanceRateFetchLoading } from "../redux/actions/FinanceInsuranceActions";
import { getModelMapValueById } from "../../../../common-deprecated/settings/utils/commonSettingUtils";

import { maximizeBuildBasket } from "../utils/basketUtils";
import { setEnableBuy, setEnableLease, setEnableReserve } from "../redux/actions/BnbSettingsActions";

export const bnbPostFetch: PostFetchFnType<BuildAndBuyStoreType> = (store) => {
    const { dispatch, getState } = store;
    const state = getState();
    const {
        navigation,
        commonSettings,
        bnbSettings,
        car,
        items,
        basket,
        save,
        externalPrice,
        modelFilter,
        model,
        grade,
        grades,
        utils,
        finance,
        retailerSettings, // This is a temporary setting. See DR-1218
    } = state;

    const modelCode =
        model.internalCode ||
        grade.model?.code ||
        car.model?.internalCode ||
        getModelMapValueById(commonSettings, navigation.model)?.internalCode ||
        "";

    const { financeProductType } = commonSettings.query;
    if (financeProductType) dispatch(setFinanceProductType(financeProductType));

    // Set finance option depending on the current config and available params.
    const showMonthlyRates = showBnbMonthlyRates(commonSettings, bnbSettings, modelCode, navigation.step, car);
    if (!retailerSettings.hideDirectMonthlyTotal && showMonthlyRates) {
        // The priority here is important: query > tyCode > defaultFinanceOption (default is cash)
        const financeOption: FinanceOptionType =
            (bnbSettings.enableFinanceCompare &&
            !finance.quotes.finance &&
            commonSettings.query.financeOption !== "monthly"
                ? "cash"
                : false) ||
            commonSettings.query.financeOption ||
            save.data?.Personal?.Parameters?.FinanceOption ||
            bnbSettings.defaultFinanceOption;
        if (financeOption !== utils.financeOption) {
            dispatch(setFinanceOption(financeOption));
        }
    }

    // Configure the isBuild setting on the utils, deprecated util field only added for backwards compat of older tridion components
    const isBuild =
        (commonSettings.body as Record<string, any>).isBuild || (commonSettings.query as Record<string, any>).isBuild;
    if (utils.isBuild !== isBuild) {
        dispatch(
            setIsBuild(
                (commonSettings.body as Record<string, any>).isBuild ||
                    (commonSettings.query as Record<string, any>).isBuild,
            ),
        );
    }

    const isPdf = commonSettings.component === COMPONENT_PDF;
    if ([NavStep.Customize, NavStep.Services, NavStep.Summary].includes(navigation.step) || isPdf) {
        // Initialize basket based on fetched data
        if (!basket.initialized) {
            // Keep in mind that the car-on-display application will load the cod/data endpoint with an identical ?path= queryparam causing the basket to be init there as well
            dispatch(initBasket(maximizeBuildBasket(car, items, basket, commonSettings.query)));
        }

        // Make sure the total cash/monthly rate prices are rendered properly.
        // For calls depending on external fetched done in front-end set the loading state so the initial render contains the loading state.
        let enableExternalPriceSetting;

        if (isPdf) {
            enableExternalPriceSetting =
                enableExternalPriceCalculation(commonSettings, bnbSettings, car) &&
                bnbSettings.priceCalculationServiceAPI;
        } else {
            enableExternalPriceSetting = enableExternalPriceCalculation(commonSettings, bnbSettings, car);
        }

        if (enableExternalPriceSetting && (!externalPrice.ready || isPdf)) {
            dispatch(enableExternalPrice(true)); // Enable external price calculation if relevant.
            dispatch(setExternalPriceFunctionFetch(true));
        } else {
            dispatch(calculatePrice());
        }

        // This will set the finance loader state when finance isn't fetched yet (in case of build spa)
        // We do this here, because we want to SSR the basket with a loading state, otherwise you will briefly see a broken price component
        // ⚠ It will not trigger the actual monthly rate to fetch! This will be done via calculatePrice above
        if (showMonthlyRates && finance.total === "0" && !bnbSettings.enableFinanceCompare) {
            dispatch(setFinanceRateFetchLoading(true));
        }
    }

    if (modelFilter.filters.length > 0) {
        dispatch(initModelFilter());
    }

    // Check if any model grades are available for checkout
    if ([NavStep.Model, NavStep.Submodels, NavStep.Grades].includes(navigation.step)) {
        const selectedGrades = navigation.step === NavStep.Grades ? grades.list : model.grades;

        const availability = {
            reserve: selectedGrades.some((selectedGrade) => selectedGrade.availableForOnlineReservation),
            buy: selectedGrades.some((selectedGrade) => selectedGrade.availableForOnlineSales),
            lease: selectedGrades.some((selectedGrade) => selectedGrade.availableForOnlineLeasing),
        };

        if (!availability.reserve && bnbSettings.enableReserve) dispatch(setEnableReserve(false));
        if (!availability.buy && bnbSettings.enableBuy) dispatch(setEnableBuy(false));
        if (!availability.lease && bnbSettings.enableLeasing) dispatch(setEnableLease(false));
    }

    return { addCacheHeaders: true };
};
